<script setup lang="ts">
import { computed, inject, ref, toRaw, watch } from "vue";
import {
  ActivityDateAnswerValue,
  ActivityDateFormElement,
  FormElementType,
} from "~/api";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

type DateShortcut = { text: string; value: () => Date };

const props = defineProps<DefaultFormComponentProps<ActivityDateFormElement>>();

// Expose the element properties using the generated model type
const elementProperties = computed(() => props.modelValue.properties);

const { useAnswer } = inject(formResponseInjectKey) as FormResponseState;
const answer = useAnswer<FormElementType.ACTIVITY_DATE>(props.modelValue);

// Parse the answer value, handling both the structured ActivityDateAnswerValue object
// and a raw date string (e.g., "2026-04-23T14:00:00+00:00" from SaveToTable enrichment).
function parseActivityDateAnswer(raw: unknown): {
  date: Date | null;
  time: string;
  reminder: string | null;
} {
  if (!raw) return { date: null, time: "08:00", reminder: null };

  if (typeof raw === "string") {
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) {
      const hours = parsed.getHours().toString().padStart(2, "0");
      const minutes = parsed.getMinutes().toString().padStart(2, "0");
      parsed.setHours(0, 0, 0, 0);
      return { date: parsed, time: `${hours}:${minutes}`, reminder: null };
    }
    return { date: null, time: "08:00", reminder: null };
  }

  const typed = raw as ActivityDateAnswerValue;
  let date: Date | null = null;
  if (typed.computedDateTime) {
    date = new Date(typed.computedDateTime);
    date.setHours(0, 0, 0, 0);
  }
  return {
    date,
    time: typed.selectedTime ?? "08:00",
    reminder: typed.reminderSetting ?? null,
  };
}

// Initialize local state from saved answer or defaults
const initialParsed = parseActivityDateAnswer(toRaw(answer.value));
const selectedDate = ref<Date | null>(initialParsed.date);
const selectedTime = ref<string | null>(initialParsed.time);
const reminderSetting = ref<string | null>(initialParsed.reminder);

// Default date picker shortcuts (can be overridden via modelValue.properties.dateShortcuts)
const defaultShortcuts = [
  {
    text: "Today",
    value: () => {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "Yesterday",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() - 1);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "7 Days Ago",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "30 Days Ago",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "90 Days Ago",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() - 90);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "Tomorrow",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "In 1 Week",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "In 30 Days",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() + 30);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
  {
    text: "In 90 Days",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() + 90);
      date.setHours(0, 0, 0, 0);
      return date;
    },
  },
];

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Convert a designer-provided shortcut value (string) into a date-producing
// function. Supported formats:
// - integer string (e.g. "7" or "-1") => offset in days from today
// - ISO date string => parsed as Date
// - keywords: "today", "tomorrow", "yesterday"
const parseShortcutValue = (val: string): (() => Date) => {
  const s = String(val).trim();
  if (/^-?\d+$/.test(s)) {
    const offset = Number.parseInt(s, 10);
    return () => {
      const d = new Date();
      d.setDate(d.getDate() + offset);
      d.setHours(0, 0, 0, 0);
      return d;
    };
  }

  const lower = s.toLowerCase();
  if (lower === "today") return () => startOfToday();
  if (lower === "tomorrow")
    return () => {
      const d = startOfToday();
      d.setDate(d.getDate() + 1);
      return d;
    };
  if (lower === "yesterday")
    return () => {
      const d = startOfToday();
      d.setDate(d.getDate() - 1);
      return d;
    };

  const parsed = Date.parse(s);
  if (!Number.isNaN(parsed)) {
    return () => {
      const d = new Date(s);
      d.setHours(0, 0, 0, 0);
      return d;
    };
  }

  // Fallback to today
  return () => startOfToday();
};

// Merged/visible shortcuts: read from element properties (designer-controlled)
// When past dates are not allowed, filter out any shortcuts that produce dates before today.
const mergedShortcuts = computed(() => {
  const today = startOfToday().getTime();
  const supplied = (() => {
    const ds = elementProperties.value?.dateShortcuts;
    // If the designer hasn't selected any shortcuts, show none.
    if (!ds || !ds.length) {
      return [] as DateShortcut[];
    }

    // Designer may store either an array of labels (string[])
    // when using the static multi-select, or an array of
    // LabelledOption objects ({ label, value }) from legacy editors.
    const first = ds[0] as any;
    if (typeof first === "string") {
      // Map selected labels to the built-in defaultShortcuts by matching text.
      // If a label isn't found, attempt to parse it as a shortcut value.
      return (ds as unknown as string[]).map((lbl) => {
        const found = defaultShortcuts.find((d) => d.text === String(lbl));
        if (found) return found;
        return { text: String(lbl), value: parseShortcutValue(String(lbl)) };
      });
    }

    // Assume objects with { label, value } (or PascalCase { Label, Value }).
    return (ds as any[]).map((s) => ({
      text: s.label ?? s.Label ?? String(s.value ?? s.Value),
      value: parseShortcutValue(
        String(s.value ?? s.Value ?? s.label ?? s.Label),
      ),
    }));
  })();

  if (elementProperties.value?.allowPastDates) {
    return supplied;
  }

  return supplied.filter((s: { text: string; value: () => Date }) => {
    try {
      const d = s.value();
      const dStart = new Date(d);
      dStart.setHours(0, 0, 0, 0);
      return dStart.getTime() >= today;
    } catch {
      return true;
    }
  });
});

// Disable dates function fed into el-date-picker: when allowPastDates is false,
// prevent selecting dates earlier than today.
const disabledDate = (date: Date) => {
  if (elementProperties.value?.allowPastDates) return false;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() < startOfToday().getTime();
};

// Time options (30-minute increments, 6:00 AM to 9:00 PM)
const timeOptions = (() => {
  const options: { value: string; label: string }[] = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) {
        break;
      }
      const hour24 = h.toString().padStart(2, "0");
      const min = m.toString().padStart(2, "0");
      const value = `${hour24}:${min}`;
      const ampm = h >= 12 ? "PM" : "AM";
      let hour12 = h;
      if (hour12 > 12) {
        hour12 = hour12 - 12;
      } else if (hour12 === 0) {
        hour12 = 12;
      }
      const label = `${hour12}:${min} ${ampm}`;
      options.push({ value, label });
    }
  }
  return options;
})();

// Reminder options
const reminderOptions = [
  { value: "None", label: "0 Minutes" },
  { value: "15Minutes", label: "15 Minutes" },
  { value: "30Minutes", label: "30 Minutes" },
  { value: "1Hour", label: "1 Hour" },
  { value: "2Hours", label: "2 Hours" },
  { value: "1Day", label: "1 Day" },
];

// Computed resolved date/time (null when inputs are not yet selected)
const computedDateTime = computed(() => {
  if (selectedDate.value === null || selectedTime.value === null) {
    return null;
  }
  const target = new Date(selectedDate.value);
  const [hours, minutes] = selectedTime.value.split(":").map(Number);
  target.setHours(hours, minutes, 0, 0);
  return target;
});

// Formatted display strings
const displayDate = computed(() => {
  if (!computedDateTime.value) {
    return "";
  }
  return computedDateTime.value.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
});

const displayTime = computed(() => {
  if (!computedDateTime.value) {
    return "";
  }
  return computedDateTime.value.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
});

// Check if selected date is in the past
const isPastDate = computed(() => {
  return computedDateTime.value ? computedDateTime.value < new Date() : false;
});

// Whether reminders are enabled for this element (null/undefined = enabled by default)
const remindersVisible = computed(
  () => elementProperties.value?.remindersEnabled !== false,
);

// Guard flag: when true, the answer watcher skips so our own optimistic writes
// don't feed back into local refs and re-trigger saveAnswer.
let isSavingLocally = false;

// Save answer whenever any input changes
const saveAnswer = () => {
  if (selectedDate.value === null || selectedTime.value === null) {
    return;
  }
  // Calculate days offset from today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDateOnly = new Date(selectedDate.value);
  selectedDateOnly.setHours(0, 0, 0, 0);
  const daysOffset = Math.round(
    (selectedDateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  const val: ActivityDateAnswerValue = {
    daysOffset: daysOffset,
    selectedTime: selectedTime.value,
    reminderSetting:
      isPastDate.value || !remindersVisible.value
        ? "None"
        : (reminderSetting.value ?? "None"),
    computedDateTime: computedDateTime.value!.toISOString(),
  };
  isSavingLocally = true;
  try {
    answer.value = val;
  } finally {
    isSavingLocally = false;
  }
};

watch([selectedDate, selectedTime, reminderSetting], saveAnswer);

// Watch for external changes to the answer (e.g., loaded asynchronously from "save to table")
// and sync them back to the local refs so the date picker is populated correctly.
// flush:'sync' ensures the callback runs synchronously inside the reactive mutation,
// so the isSavingLocally guard works (it's still true when our own writes fire this).
watch(
  answer,
  (newVal) => {
    if (isSavingLocally) return;
    if (!newVal) return;
    const raw = toRaw(newVal);
    const parsed = parseActivityDateAnswer(raw);
    if (!parsed.date) return;

    if (
      !selectedDate.value ||
      selectedDate.value.getTime() !== parsed.date.getTime()
    ) {
      selectedDate.value = parsed.date;
    }
    if (parsed.time !== selectedTime.value) {
      selectedTime.value = parsed.time;
    }
    // Only sync reminder from structured answers — bare date strings (from
    // SaveToTable enrichment) don't carry reminder info and would clobber
    // the local selection with null.
    if (
      typeof raw !== "string" &&
      (parsed.reminder ?? "None") !== (reminderSetting.value ?? "None")
    ) {
      reminderSetting.value = parsed.reminder;
    }
  },
  { flush: "sync" },
);
</script>

<template>
  <div class="ActivityDateComponent">
    <slot name="label" :element="modelValue" />
    <div class="activity-date-inputs">
      <div class="input-group">
        <div class="input-row">
          <el-date-picker
            v-model="selectedDate"
            :disabled="isReadOnly"
            type="date"
            placeholder="Select date"
            :shortcuts="mergedShortcuts"
            :disabled-date="disabledDate"
            class="date-picker"
          />

          <el-select
            v-model="selectedTime"
            :disabled="isReadOnly"
            placeholder="Select time"
            class="time-select"
          >
            <el-option
              v-for="opt in timeOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </el-select>

          <span v-if="remindersVisible && !isPastDate" class="reminder-label"
            >Reminder</span
          >

          <el-select
            v-if="remindersVisible && !isPastDate"
            v-model="reminderSetting"
            :disabled="isReadOnly"
            placeholder="Reminder"
            class="reminder-select"
          >
            <el-option
              v-for="opt in reminderOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </el-select>
        </div>
      </div>

      <div v-if="computedDateTime" class="computed-display">
        <span class="computed-item">📅 {{ displayDate }}</span>
        <span class="computed-item">⏰ {{ displayTime }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.ActivityDateComponent {
  display: flex;
  gap: 8px;
  justify-content: space-between;

  .activity-date-inputs {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .input-label {
    font-size: 0.85em;
    color: #606266;
    font-weight: 500;
  }

  .input-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .date-picker {
    width: 200px;
  }

  .time-select {
    width: 140px;
  }

  .reminder-label {
    font-size: 0.9em;
    color: #606266;
    font-weight: 500;
  }

  .reminder-select {
    width: 160px;
  }

  .computed-display {
    display: flex;
    flex-direction: row;
    gap: 20px;
    padding: 6px 0;
    font-size: 0.9em;
    color: #303133;
  }

  .computed-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}
</style>
